import { Component, Input, OnChanges } from '@angular/core';
import { ReportColumn } from '../../shared/data-table/data-table.component';
import { mapDriverTimelinessStackedRows, timelinessSegments, timelinessStatItems, topItems } from '../../utils/chart-helpers';
import { CHART_COLORS } from '../../utils/chart-theme';
import { driverDisplayName, driverProfileImage, pct } from '../../utils/kpi-helpers';

export type TimelinessScoringMode = 'merged' | 'pickup' | 'dropoff';

export const TIMELINESS_SCORING_OPTIONS: Array<{ value: TimelinessScoringMode; label: string }> = [
  { value: 'merged', label: 'Pickup + Drop-off' },
  { value: 'pickup', label: 'Pickup Time' },
  { value: 'dropoff', label: 'Drop-off Time' }
];

const MODE_SCOPE: Record<TimelinessScoringMode, string> = {
  merged: 'combined pickup and drop-off',
  pickup: 'pickup arrival',
  dropoff: 'drop-off / appointment time'
};

@Component({
  selector: 'app-br-timeliness-tab',
  templateUrl: './timeliness-tab.component.html'
})
export class BrTimelinessTabComponent implements OnChanges {
  @Input() loading: boolean;
  @Input() refreshing: boolean;
  @Input() error: string;
  @Input() data: any;
  @Input() scoringMode: TimelinessScoringMode = 'merged';

  readonly secondaryColor = CHART_COLORS[1];

  statItems: any[] = [];
  tripSegments: any[] = [];
  onTimeRateItems: any[] = [];
  tripVolumeItems: any[] = [];
  stackedDriverItems: any[] = [];
  drivers: any[] = [];

  columns: ReportColumn[] = [
    {
      key: 'driver',
      header: 'Driver',
      type: 'avatar',
      avatarName: (row) => driverDisplayName(row.driver),
      avatarImage: (row) => driverProfileImage(row),
      avatarVariant: 'driver'
    },
    { key: 'onTimeTrips', header: 'On-Time', value: true },
    { key: 'earlyTrips', header: 'Early', value: true },
    { key: 'lateTrips', header: 'Late', value: true },
    {
      key: 'onTime',
      header: 'On-Time %',
      type: 'score',
      value: true,
      scoreValue: (row) => Number(row.onTime != null ? row.onTime : 0),
      render: (row) => pct(row.onTime)
    },
    { key: 'total', header: 'Completed Trips', value: true }
  ];

  get modeScope(): string {
    return MODE_SCOPE[this.scoringMode] || MODE_SCOPE.merged;
  }

  get summaryDescription(): string {
    return `Shows on-time, early, and late % for ${this.modeScope}.`;
  }

  get mixDescription(): string {
    return `How many finished trips were on time, early, or late using ${this.modeScope}.`;
  }

  get onTimeRateDescription(): string {
    return `Drivers with the best on-time % based on ${this.modeScope}.`;
  }

  get tripVolumeDescription(): string {
    return `Drivers with the most finished trips counted for ${this.modeScope}.`;
  }

  get stackedDescription(): string {
    return `For each driver, finished trips broken into on time, early, and late (${this.modeScope}).`;
  }

  ngOnChanges() {
    if (!this.data) return;
    const kpiCards = this.data.kpiCards || {};
    const drivers = this.data.drivers || [];

    this.statItems = timelinessStatItems(kpiCards);
    this.tripSegments = timelinessSegments(drivers);
    this.onTimeRateItems = topItems(drivers.map((row: any) => ({ label: driverDisplayName(row.driver), value: Number(row.onTime) || 0 })));
    this.tripVolumeItems = topItems(drivers.map((row: any) => ({ label: driverDisplayName(row.driver), value: Number(row.total) || 0 })));
    this.stackedDriverItems = topItems(mapDriverTimelinessStackedRows(drivers), 6);
    this.drivers = drivers;
  }

  pctFormatter = (v: number) => `${Math.round(v)}%`;
}
